// src/pages/GuideAnalytics.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints } from '../services/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { getFriendlyError } from '../components/errorUtils';
import { FREE_MODE } from '../config/freeMode';
import Mascot from '../components/Mascot';
import '../styles/pages/Analytics.css';


// ── Helpers ────────────────────────────────────────────────────────────────────

function canAccess(feature, plan) {
  if (FREE_MODE) return true;
  const rules = {
    total_plays:      ['free', 'individual', 'teams'],
    last_played:      ['free', 'individual', 'teams'],
    completion_rate:  ['individual', 'teams'],
    avg_time:         ['individual', 'teams'],
    unique_viewers:   ['individual', 'teams'],
    dropoff_chart:    ['individual', 'teams'],
    play_history:     ['individual', 'teams'],
    trend_chart:      ['teams'],
    member_breakdown: ['teams'],
    benchmarking:     ['teams'],
    health_score:     ['teams'],
  };
  return rules[feature]?.includes(plan) ?? false;
}

function formatTime(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatRelativeDate(isoString) {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? 's' : ''} ago`;
}

function formatDate(isoString) {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Count-up animation hook ────────────────────────────────────────────────────

function useCountUp(target, duration = 800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) return;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return value;
}


// ── Theme colors for recharts (SVG attributes don't resolve var()) ────────────

function readChartColors() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name, fallback) => cs.getPropertyValue(name).trim() || fallback;
  return {
    ink: v('--text-primary', '#121417'),
    dim: v('--text-dim', '#6E757D'),
    rule: v('--border-color', '#C9CED4'),
    accent: v('--primary-accent', '#E8521C'),
  };
}

function useChartColors() {
  const [colors, setColors] = useState(readChartColors);
  useEffect(() => {
    // Re-read when the theme toggles (ThemeContext sets html[data-theme]).
    const mo = new MutationObserver(() => setColors(readChartColors()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);
  return colors;
}

const pad2 = (n) => String(n).padStart(2, '0');

const LockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ── LockedSection ─────────────────────────────────────────────────────────────

function LockedSection({ requiredPlan, featureName, children }) {
  const navigate = useNavigate();
  const label = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
  return (
    <div className="an-locked">
      <div className="an-locked__content" aria-hidden="true">{children}</div>
      <div className="an-locked__veil">
        <LockIcon size={22} />
        <p>{featureName} requires {label} plan</p>
        <button className="lp-btn sm" onClick={() => navigate('/#pricing')}>
          Upgrade to {label} →
        </button>
      </div>
    </div>
  );
}

// ── Skeleton (same shape as the loaded page) ──────────────────────────────────

function LoadingSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading analytics">
      <div className="an-stats">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="an-stat">
            <span className="an-skel" style={{ height: 11, width: '55%' }} />
            <span className="an-skel" style={{ height: 30, width: '45%' }} />
          </div>
        ))}
      </div>
      <div className="an-section">
        <span className="an-skel" style={{ height: 20, width: 160, marginBottom: 18 }} />
        <div className="m-sheet an-sheet">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '11px 0' }}>
              <span className="an-skel" style={{ width: 24, height: 12 }} />
              <span className="an-skel" style={{ flex: 1, height: 10 }} />
              <span className="an-skel" style={{ width: 110, height: 12 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Stat (one cell of the ruled ledger) ───────────────────────────────────────

function Stat({ label, value, locked, lockLabel, index, isKey }) {
  return (
    <div className={`an-stat${isKey ? ' an-stat--key' : ''}`} style={{ '--i': index }}>
      <span className="m-eyebrow">{label}</span>
      {locked ? (
        <span className="an-stat__locked"><LockIcon />{lockLabel}</span>
      ) : (
        <span className="an-stat__value">{value}</span>
      )}
    </div>
  );
}

// ── Drop-off Chart ────────────────────────────────────────────────────────────

function DropoffChart({ stepDropoff, maxReached }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 100); return () => clearTimeout(t); }, []);

  // The single step that loses the most people gets the orange mark.
  let worst = -1;
  stepDropoff.forEach((step, i) => {
    if (i > 0 && step.drop_rate > 0 && (worst < 0 || step.drop_rate > stepDropoff[worst].drop_rate)) worst = i;
  });

  return (
    <ol className="an-drop">
      {stepDropoff.map((step, i) => {
        const pct = maxReached > 0 ? step.reached / maxReached : 0;
        const isWorst = i === worst;
        return (
          <li key={step.step_number} className={isWorst ? 'is-worst' : ''} style={{ '--i': i }}>
            <span className="an-drop__n" aria-label={`Step ${step.step_number}`}>{pad2(step.step_number)}</span>
            <span className="an-drop__track">
              <span className="an-drop__bar" style={{ transform: `scaleX(${mounted ? pct : 0})` }} />
            </span>
            <span className="an-drop__meta">
              {step.repair_rate > 0.20 && (
                <span className="m-badge m-badge--private an-drop__repair" title="High repair rate: many viewers needed help finding this element">
                  Repairs
                </span>
              )}
              {step.reached} reached · {Math.round(pct * 100)}%
            </span>
            {isWorst && <span className="m-hand an-drop__flag">biggest drop, {Math.round(step.drop_rate * 100)}% stop here</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ── Play History ──────────────────────────────────────────────────────────────

function PlayHistory({ history }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? history : history.slice(0, 10);

  return (
    <div>
      <ul className="an-rows">
        {visible.map((session, i) => (
          <li key={i} style={{ '--i': i }}>
            <span className={`an-dot${session.completed ? '' : ' an-dot--bad'}`} aria-hidden="true" />
            <span className="m-mono">{formatDate(session.played_at)}</span>
            <span className="an-rows__status">
              {session.completed
                ? 'Completed'
                : `Abandoned at step ${session.steps_completed}`}
            </span>
            <span className="an-rows__time">
              {session.completed ? formatTime(session.time_seconds) : '—'}
            </span>
          </li>
        ))}
      </ul>
      {history.length > 10 && !showAll && (
        <button className="lp-btn sm quiet an-more" onClick={() => setShowAll(true)}>
          Show {history.length - 10} more
        </button>
      )}
    </div>
  );
}

// ── Custom Tooltip for trend chart ────────────────────────────────────────────

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="an-tip">
      <span className="m-mono">{label}</span>
      <strong>{payload[0].value}</strong> plays
    </div>
  );
}

function TrendChart({ data, height, bare = false }) {
  const c = useChartColors();
  const last = data.length - 1;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 12, left: bare ? 0 : -20, bottom: 0 }}>
        {!bare && <CartesianGrid stroke={c.rule} strokeDasharray="2 4" vertical={false} />}
        {!bare && (
          <XAxis
            dataKey="date" tick={{ fill: c.dim, fontSize: 11 }} axisLine={false} tickLine={false}
            tickFormatter={d => { const dt = new Date(d); return `${dt.getMonth() + 1}/${dt.getDate()}`; }}
          />
        )}
        {!bare && <YAxis tick={{ fill: c.dim, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />}
        {!bare && <Tooltip content={<TrendTooltip />} cursor={{ stroke: c.rule }} />}
        <Area
          type="monotone" dataKey="plays" stroke={c.ink} strokeWidth={1.75}
          fill={c.ink} fillOpacity={0.07} isAnimationActive={!bare}
          dot={(props) => props.index === last
            ? <circle key="end" cx={props.cx} cy={props.cy} r={4} fill={c.accent} stroke="none" />
            : <g key={props.index} />}
          activeDot={{ r: 4, fill: c.ink, stroke: 'none' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Member Breakdown Table ────────────────────────────────────────────────────

function MemberBreakdown({ members }) {
  const statusBadge = {
    completed:   <span className="m-badge m-badge--shared">Completed</span>,
    in_progress: <span className="m-badge m-badge--private">In progress</span>,
    not_started: <span className="an-note">Not started</span>,
  };

  return (
    <div className="an-tablewrap">
      <table className="an-table">
        <thead>
          <tr><th>Member</th><th>Status</th><th>Time</th><th>Last attempt</th></tr>
        </thead>
        <tbody>
          {members.map(m => (
            <tr key={m.user_id}>
              <td className="an-ellipsis">{m.email}</td>
              <td>{statusBadge[m.status]}</td>
              <td className="m-mono">{formatTime(m.completion_time_seconds)}</td>
              <td>{formatRelativeDate(m.last_attempt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Time Benchmarking ─────────────────────────────────────────────────────────

function Benchmarking({ data }) {
  const { fastest_seconds, slowest_seconds, average_seconds, members } = data;
  const avgPct = slowest_seconds > 0 ? (average_seconds / slowest_seconds) * 100 : 50;

  return (
    <div className="an-bench">
      {members.map(m => {
        const pct = slowest_seconds > 0 ? (m.seconds / slowest_seconds) * 100 : 50;
        const tagText = m.relative === 'fast' ? 'faster than avg' : m.relative === 'slow' ? 'slower than avg' : 'near avg';
        const cls = m.relative === 'fast' ? ' is-fast' : m.relative === 'slow' ? ' is-slow' : '';
        return (
          <div key={m.email} className={`an-bench__row${cls}`}>
            <span className="an-bench__who">{m.email}</span>
            <span className="an-bench__track">
              <span className="an-bench__bar" style={{ width: `${pct}%` }} />
              <span className="an-bench__avg" style={{ left: `${avgPct}%` }} title={`Average ${formatTime(average_seconds)}`} />
            </span>
            <span className="an-bench__time">{formatTime(m.seconds)}</span>
            <span className="an-bench__tag">{tagText}</span>
          </div>
        );
      })}
      <div className="an-bench__foot">
        <span>Fastest {formatTime(fastest_seconds)}</span>
        <span>Average {formatTime(average_seconds)} (dashed line)</span>
        <span>Slowest {formatTime(slowest_seconds)}</span>
      </div>
    </div>
  );
}

// ── Health Score ──────────────────────────────────────────────────────────────

function HealthScore({ data }) {
  if (!data) return <p className="an-note">No health data yet. It appears after the guide has been replayed a few times.</p>;
  const { overall_repair_rate, rating, problematic_steps = [] } = data;
  const pct = Math.round(overall_repair_rate * 100);
  const label = {
    healthy: 'Healthy',
    needs_attention: 'Needs attention',
    critical: 'Critical',
  }[rating] || rating;

  return (
    <div className={`an-health--${rating}`}>
      <div className="an-health">
        <div>
          <p className="an-health__word">{label}</p>
          <p className="an-health__sub">
            <span className="m-mono">{pct}%</span> of replays triggered the repair overlay
          </p>
        </div>
      </div>
      {problematic_steps.length === 0 ? (
        <p className="an-note" style={{ marginTop: 16 }}>No issues detected. All steps are working.</p>
      ) : (
        <ul className="an-health__list">
          {problematic_steps.map(s => (
            <li key={s.step_number}>
              <span className="m-mono" style={{ color: 'var(--primary-accent)' }}>{pad2(s.step_number)}</span>
              <span>{Math.round(s.repair_rate * 100)}% of replays needed manual help</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState() {
  const navigate = useNavigate();
  return (
    <div className="m-sheet an-empty">
      <Mascot size={48} />
      <div>
        <h2>No plays yet</h2>
        <p>Play this guide using the extension and data will appear here. You can also share it from My Guides so others can play it.</p>
        <button className="lp-btn sm" onClick={() => navigate('/my-guides')}>← Back to My Guides</button>
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, subtitle, children }) {
  return (
    <section className="an-section">
      <div className="an-section__head">
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const GuideAnalytics = () => {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const totalPlaysAnimated = useCountUp(data?.total_plays ?? 0);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    let cancelled = false;
    setLoading(true);
    fetch(endpoints.analytics.guide(guideId), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async r => {
        if (cancelled) return;
        if (r.status === 403) { setError(getFriendlyError('403','Guide Analytics')); return; }
        if (r.status === 404) { setError(getFriendlyError('404','Guide Analytics')); return; }
        if (!r.ok) { setError(getFriendlyError('error','Guide Analytics')); return; }
        setData(await r.json());
      })
      .catch(() => { if (!cancelled) setError(getFriendlyError('error','Guide Analytics')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [guideId, token, navigate]);

  const plan = data?.plan ?? user?.plan ?? 'free';

  const errorCopy = {
    '403': "You don't have access to this guide's analytics.",
    '404': 'Guide not found.',
    error: 'Failed to load analytics. Please try again.',
  };

  return (
    <div className="an-page">
      <button className="an-back" onClick={() => navigate('/my-guides')}>← My Guides</button>

      <header className="m-page-head an-head">
        <div>
          <span className="m-eyebrow">Guide analytics</span>
          <h1>Analytics</h1>
          {data?.guide_title && <p>{data.guide_title}</p>}
        </div>
      </header>

      {loading && <LoadingSkeleton />}

      {!loading && error && (
        <div className="an-error" role="alert">
          <p>{errorCopy[error] ?? error}</p>
          <p><button className="an-back" style={{ margin: 0 }} onClick={() => navigate('/my-guides')}>Back to My Guides</button></p>
        </div>
      )}

      {!loading && !error && data && (
        data.total_plays === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Stat ledger */}
            <div className="an-stats">
              <Stat index={0} label="Total plays" value={totalPlaysAnimated} />
              <Stat index={1} label="Last played" value={formatRelativeDate(data.last_played_at)} />
              <Stat
                index={2} label="Unique viewers"
                value={canAccess('unique_viewers', plan) ? (data.unique_viewers ?? 0) : null}
                locked={!canAccess('unique_viewers', plan)} lockLabel="Individual plan"
              />
              <Stat
                index={3} label="Completion rate" isKey
                value={canAccess('completion_rate', plan) ? `${Math.round((data.completion_rate ?? 0) * 100)}%` : null}
                locked={!canAccess('completion_rate', plan)} lockLabel="Individual plan"
              />
              <Stat
                index={4} label="Avg completion"
                value={canAccess('avg_time', plan) ? formatTime(data.average_completion_time_seconds) : null}
                locked={!canAccess('avg_time', plan)} lockLabel="Individual plan"
              />
            </div>

            {/* Drop-off Chart */}
            <Section title="Step drop-off" subtitle="Where people stop following the guide">
              {canAccess('dropoff_chart', plan) ? (
                data.step_dropoff?.length > 0 ? (
                  <div className="m-sheet an-sheet">
                    <DropoffChart stepDropoff={data.step_dropoff} maxReached={data.step_dropoff[0]?.reached ?? 1} />
                  </div>
                ) : (
                  <p className="an-note">No step data yet.</p>
                )
              ) : (
                <LockedSection requiredPlan="individual" featureName="Step drop-off">
                  <div className="m-sheet an-sheet">
                    <DropoffChart
                      stepDropoff={[
                        { step_number: 1, reached: 47, drop_rate: 0.04, repair_rate: 0.02 },
                        { step_number: 2, reached: 45, drop_rate: 0.11, repair_rate: 0.05 },
                        { step_number: 3, reached: 40, drop_rate: 0.28, repair_rate: 0.22 },
                        { step_number: 4, reached: 29, drop_rate: 0.10, repair_rate: 0.03 },
                      ]}
                      maxReached={47}
                    />
                  </div>
                </LockedSection>
              )}
            </Section>

            {/* Play History */}
            <Section title="Play history">
              {canAccess('play_history', plan) ? (
                data.play_history?.length > 0 ? (
                  <PlayHistory history={data.play_history} />
                ) : (
                  <p className="an-note">No sessions yet.</p>
                )
              ) : (
                <LockedSection requiredPlan="individual" featureName="Play history">
                  <PlayHistory history={[
                    { played_at: new Date(Date.now()-3600000).toISOString(), completed: true, time_seconds: 198, steps_completed: 5, total_steps: 5 },
                    { played_at: new Date(Date.now()-86400000).toISOString(), completed: false, time_seconds: null, steps_completed: 2, total_steps: 5 },
                    { played_at: new Date(Date.now()-172800000).toISOString(), completed: true, time_seconds: 245, steps_completed: 5, total_steps: 5 },
                  ]} />
                </LockedSection>
              )}
            </Section>

            {/* 30-day Trend (Teams only) */}
            <Section title="Last 30 days" subtitle="Plays per day">
              {canAccess('trend_chart', plan) ? (
                data.trend_30d?.length > 0 ? (
                  <div className="m-sheet an-sheet">
                    <TrendChart data={data.trend_30d} height={220} />
                  </div>
                ) : (
                  <p className="an-note">No data in the last 30 days.</p>
                )
              ) : (
                <LockedSection requiredPlan="teams" featureName="30-day trend">
                  <div className="m-sheet an-sheet">
                    <TrendChart bare height={160} data={[{date:'',plays:2},{date:'',plays:5},{date:'',plays:3},{date:'',plays:8},{date:'',plays:6},{date:'',plays:11},{date:'',plays:7}]} />
                  </div>
                </LockedSection>
              )}
            </Section>

            {/* Member Breakdown (Teams only) */}
            <Section title="Team completion">
              {canAccess('member_breakdown', plan) ? (
                data.member_breakdown?.length > 0 ? (
                  <MemberBreakdown members={data.member_breakdown} />
                ) : (
                  <p className="an-note">No team members have played this guide yet.</p>
                )
              ) : (
                <LockedSection requiredPlan="teams" featureName="Team completion">
                  <MemberBreakdown members={[
                    { user_id: 1, email: 'sarah@example.com', status: 'completed', completion_time_seconds: 170, last_attempt: new Date(Date.now()-3600000).toISOString(), attempts: 1 },
                    { user_id: 2, email: 'james@example.com', status: 'in_progress', completion_time_seconds: null, last_attempt: new Date(Date.now()-7200000).toISOString(), attempts: 2 },
                    { user_id: 3, email: 'nina@example.com', status: 'not_started', completion_time_seconds: null, last_attempt: null, attempts: 0 },
                  ]} />
                </LockedSection>
              )}
            </Section>

            {/* Benchmarking (Teams only) */}
            {(canAccess('benchmarking', plan) ? data.benchmarking?.members?.length > 0 : true) && (
              <Section title="Time benchmarking" subtitle="How long each teammate took to finish">
                {canAccess('benchmarking', plan) ? (
                  data.benchmarking ? (
                    <div className="m-sheet an-sheet"><Benchmarking data={data.benchmarking} /></div>
                  ) : (
                    <p className="an-note">Not enough completion data yet.</p>
                  )
                ) : (
                  <LockedSection requiredPlan="teams" featureName="Time benchmarking">
                    <div className="m-sheet an-sheet">
                      <Benchmarking data={{
                        fastest_seconds: 170, slowest_seconds: 570, average_seconds: 310,
                        members: [
                          { email: 'sarah@example.com', seconds: 170, relative: 'fast' },
                          { email: 'james@example.com', seconds: 310, relative: 'average' },
                          { email: 'nina@example.com', seconds: 570, relative: 'slow' },
                        ],
                      }} />
                    </div>
                  </LockedSection>
                )}
              </Section>
            )}

            {/* Health Score (Teams only) */}
            <Section title="Guide health" subtitle="How often replays needed help finding an element">
              {canAccess('health_score', plan) ? (
                <div className="m-sheet an-sheet"><HealthScore data={data.health_score} /></div>
              ) : (
                <LockedSection requiredPlan="teams" featureName="Guide health score">
                  <div className="m-sheet an-sheet">
                    <HealthScore data={{ overall_repair_rate: 0.08, rating: 'healthy', problematic_steps: [] }} />
                  </div>
                </LockedSection>
              )}
            </Section>

            {/* Workspace rank (Teams only) */}
            {canAccess('trend_chart', plan) && data.workspace_rank && (
              <div className="an-rank">
                <span>This guide ranks <strong>#{data.workspace_rank}</strong> in your workspace by plays</span>
                <span className="m-mono">{data.workspace_total_plays} total workspace plays</span>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
};

export default GuideAnalytics;
